import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initCompany } from "../utils/data/initData";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Box } from "@mui/system";
import NavigationPanel from "../components/TaxComputationConsole/NavigationPanel";
import { AuthContext } from "../contexts/AuthContext";
import Loading from "../components/App/Loading";
import { useSuccessDialog } from "../contexts/SuccessDialogContext";
import { useErrorDialog } from "../contexts/ErrorDialogContext";
import ConsolePanel from "../components/TaxComputationConsole/ConsolePanel";
import { useTheme } from "@emotion/react";
import HirePurchasePage from "./Schedules/CA/HirePurchaseSchedule";
import WorkInProgress from "../components/App/WorkInProgress";
import DetailedPL from "./DetailedP&L";
import ContentPage from "./ContentPage";
import CustomDialog from "../components/utils/dialog/customDialog";
import AccessDenied from "../components/Common/AccessDenied";

import DummySchedule from "./Schedules/DummySchedule";
import InvestmentSavySchedule from "./Schedules/InvestmentSavySchedule";
import RentalSchedulePage from "./Schedules/Others/RentalSchedule";
import DonationSchedulePage from "./Schedules/Others/Donation";
import FurtherDeductionSchedulePage from "./Schedules/Others/FurtherDeduction";
import MedicalExpenseSchedulePage from "./Schedules/Others/MedicalExpense";
import MovementSchedulePage from "./Schedules/Others/Movement";
import OtherIncomeSchedulePage from "./Schedules/Others/OtherIncome";
import RenovationSchedulePage from "./Schedules/Others/Renovation";
import InterestExpenseSchedulePage from "./Schedules/Others/InterestExpense";
import InvestmentIncomeSchedulePage from "./Schedules/Others/InvestIncome";
import PartialBreakdownPage from "./Schedules/PartialBreakdown";
import FullBreakdownPage from "./Schedules/FullBreakdown";
import FaDisposalSchedulePage from "./Schedules/CA/FaDisposalSchedule";
import FaSummarySchedulePage from "./Schedules/CA/FaSummarySchedule";
import FaAdditionsSchedulePage from "./Schedules/CA/FaAdditionsSchedule";
import FaRegisterSchedulePage from "./Schedules/CA/FaRegisterSchedule";
import CaSchedulePage from "./Schedules/CA/CaSchedule";
import CaSummaryPage from "./Schedules/CA/CASummary";
import AutoSaveConfirmationDialog from "../components/utils/dialog/autoSaveConfirmationDialog";
import NavigationConfirmationDialog from "../components/utils/dialog/navigationConfirmationDialog";
import { handleSave } from "../utils/data/saveData";
import ScheduleCreationDialog from "../components/utils/dialog/schedulesCreationDialog";
import CAScheduleCreationDialog from "../components/utils/dialog/CASchedulesCreationDialog";
import { navItems } from "../components/TaxComputationConsole/utils/navigationSettings";
import MainTaxComputation from "./MainTaxComputation";
import { ScheduleTaggingProvider } from "../components/Common/TaxAdjustment";
import ForeignIncomeTracker from "./Schedules/ForeignIncome/ForeignIncomeTracker";
import ForeignTaxCredit from "./Schedules/ForeignIncome/ForeignTaxCredit";
import ForeignTradeSchedulePage from "./Schedules/Others/ForeignTrade";
import AllocationExpensesSchedulePage from "./Schedules/Others/AllocationExpenses";
import ServiceIncomeSchedulePage from "./Schedules/Others/ServiceIncome";
import SharedBasedPaymentSchedulePage from "./Schedules/Others/SharedBasedPayment";
import PageLoadingOverlay from "../components/TaxComputationConsole/PageLoadingOverlay";
import GlobalDisclosureLibrary from "../components/TaxComputationConsole/GlobalDisclosureLibrary";
import { getCaSettings } from "../builders/settings/CaSetting";

/**
 * Controls component rendering based on company type
 * @param {Object} company - The company object with companyType property
 * @param {string} componentName - The name of the component to render
 * @returns {boolean} - Whether the component should be rendered
 */
const shouldRenderComponent = (company, componentName) => {
  // Handle object-based components (schedules)
  if (componentName !== null && typeof componentName === "object") {
    return true;
  }

  // Handle string-based component names
  switch (componentName) {
    case "Content Page":
      return true; // Always available
    case "Main Computation":
      return true; // Always available
    case "Detailed P&L":
      return true; // Always available
    case "CA Schedules":
      return true;
    default:
      return true; // Default: allow rendering
  }
};

const scheduleComponents = {
  Savy: DummySchedule,
  InvestmentSavy: InvestmentSavySchedule,
  PartialBreakdown: PartialBreakdownPage,
  FullBreakdown: FullBreakdownPage,
  Rental: RentalSchedulePage,
  OtherIncome: OtherIncomeSchedulePage,
  OtherInvestmentIncome: InvestmentIncomeSchedulePage,
  AllocationOfExpenses: AllocationExpensesSchedulePage,
  MedicalExpense: MedicalExpenseSchedulePage,
  FurtherDeduction: FurtherDeductionSchedulePage,
  InterestExpense: InterestExpenseSchedulePage,
  Donation: DonationSchedulePage,
  Movement: MovementSchedulePage,
  Renovation: RenovationSchedulePage,
  ForeignTrade: ForeignTradeSchedulePage,
  UnremittedForeignIncome: ForeignIncomeTracker,
  ForeignTaxCredit: ForeignTaxCredit,
  ServiceIncome: ServiceIncomeSchedulePage,
  ShareBasedPayment: SharedBasedPaymentSchedulePage,
};

const CAScheduleComponents = {
  FARegister: FaRegisterSchedulePage,
  FASummary: FaSummarySchedulePage,
  FAAdditions: FaAdditionsSchedulePage,
  FADisposals: FaDisposalSchedulePage,
  S19Schedule: CaSchedulePage,
  S19ASchedule: CaSchedulePage,
  S19BSchedule: CaSchedulePage,
  S19DSchedule: CaSchedulePage,
  S16Schedule: CaSchedulePage,
  HirePurchase: HirePurchasePage,
  CASummary: CaSummaryPage,
};

const TaxComputationConsole = () => {
  const location = useLocation();
  const states = location.state || {};

  const company = initCompany(states);

  const theme = useTheme();

  const authContext = useContext(AuthContext);
  const {
    checkAccess,
    registerCompanyRole,
    clearCompanyRole,
    getCompanyRole,
    createPLSchedule,
    createCASchedule,
    getStatementDoc,
    getSchedules,
    getSchedulesWithSavedDoc,
    getCaSchedulesWithSavedDoc,
    getCAScheduleList,
    getCASummaryPDFDetails,
    getCASettings,
    reorderScheduleNo,
    reorderCAScheduleNo,
  } = authContext;

  const fetchCASummaryPdfDetails = async () => {
    try {
      const fetchedDetails = await getCASummaryPDFDetails(company.tc_id);
      return fetchedDetails;
    } catch (error) {
      handleError(error);
      return null;
    }
  };

  const getPlScheduleDoc = async () => {
    try {
      const fetchedSchedules = await getSchedulesWithSavedDoc(company.tc_id);
      return fetchedSchedules;
    } catch (error) {
      handleError(error);
      return schedules;
    }
  };

  const getCaScheduleDoc = async () => {
    try {
      const fetchedSchedules = await getCaSchedulesWithSavedDoc(company.tc_id);
      return fetchedSchedules;
    } catch (error) {
      handleError(error);
      return schedules;
    }
  };

  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const getCurrentTab = () => tabs[activeTab] || {};
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [disclosureLibraryOpen, setDisclosureLibraryOpen] = useState(false);
  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const [schedules, setSchedules] = useState([]);
  const [CASchedules, setCASchedules] = useState([]);
  const [caSettings, setCaSettings] = useState(() => getCaSettings());
  const [isFetchingSchedules, setIsFetchingSchedules] = useState(false);
  const [isFetchingCASchedules, setIsFetchingCASchedules] = useState(false);

  const [loading, setLoading] = useState(true);
  const [accessLoading, setAccessLoading] = useState(true);
  const [isReorderingSchedules, setIsReorderingSchedules] = useState(false);

  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();

  useEffect(() => {
    let isActive = true;
    const roleFallback = "admin";

    const loadCompanyRole = async () => {
      clearCompanyRole();
      setAccessLoading(true);

      if (!company.c_id) {
        registerCompanyRole(roleFallback, null);
        setAccessLoading(false);
        return;
      }

      try {
        const data = await getCompanyRole({ c_id: company.c_id });
        if (isActive) {
          registerCompanyRole(data?.role ?? roleFallback, company.c_id);
          setAccessLoading(false);
        }
      } catch {
        if (isActive) {
          registerCompanyRole(roleFallback, company.c_id);
          setAccessLoading(false);
        }
      }
    };

    loadCompanyRole();

    return () => {
      isActive = false;
      clearCompanyRole();
    };
  }, [company.c_id, clearCompanyRole, getCompanyRole, registerCompanyRole]);

  const initializeSchedules = async () => {
    setIsFetchingSchedules(true);
    setIsFetchingCASchedules(true);
    try {
      const fetchedSchedules = await getSchedules(company.tc_id);
      setSchedules(fetchedSchedules);

      const fetchedCASchedules = await getCAScheduleList(company.tc_id);
      setCASchedules(fetchedCASchedules);

      const fetchedCaSettings = await getCASettings();
      setCaSettings(fetchedCaSettings?.caSettings || getCaSettings());
    } catch (error) {
      handleError(error);
    }
    setIsFetchingSchedules(false);
    setIsFetchingCASchedules(false);
  };

  const refreshSchedules = async (scheduleType, tc_id) => {
    if (scheduleType === "CA") {
      const fetchedCASchedules = await getCAScheduleList(tc_id);
      setCASchedules(fetchedCASchedules);
    } else {
      const fetchedSchedules = await getSchedules(tc_id);
      setSchedules(fetchedSchedules);
    }
  };

  useEffect(() => {
    setLoading(true);
    initializeSchedules();
    setLoading(false);
  }, []);

  useEffect(() => {
    try {
      const savedSection = localStorage.getItem("selectedSection");
      const savedComponent = localStorage.getItem("selectedComponent");
      const savedScheduleType = localStorage.getItem("scheduleType");

      if (savedComponent) {
        if (
          savedSection &&
          (savedSection === "Schedule" || savedSection === "CA Schedule")
        ) {
          let parsedComponent;
          try {
            parsedComponent = JSON.parse(savedComponent);
          } catch (error) {
            handleError(error);
            parsedComponent = null; // Fallback to prevent crashes
          }

          if (parsedComponent) {
            // Ensure the tab is opened only if not already present
            const existingTabIndex = tabs.findIndex(
              (tab) => tab.key === parsedComponent.id
            );
            if (existingTabIndex === -1) {
              if (savedSection === "Schedule") {
                handleOpenSchedule("P&L", parsedComponent, savedScheduleType);
              } else if (savedSection === "CA Schedule") {
                handleOpenSchedule("CA", parsedComponent, savedScheduleType);
              }
            } else {
              setActiveTab(existingTabIndex);
            }
            return;
          }
        } else {
          // Restore non-schedule tabs
          const savedTab = navItems.find(
            (item) => item.component === savedComponent
          );
          if (savedTab) {
            setTabs([
              {
                label: savedTab.label,
                component: savedTab.component,
                sectionKey: savedTab.sectionKey,
              },
            ]);
            setActiveTab(0);
            return;
          }
        }
      }
    } catch (error) {
      handleError(error);
    }

    // Default fallback to "Content Page" if anything fails
    setTabs([
      {
        label: "Content Page",
        component: "Content Page",
        sectionKey: "contentPage",
      },
    ]);
    setActiveTab(0);
  }, [company.uen, company.tc_id]);

  // Save currently active tab to localStorage
  useEffect(() => {
    const isObject =
      typeof tabs[activeTab]?.component === "object" &&
      tabs[activeTab]?.component !== null &&
      !Array.isArray(tabs[activeTab]?.component);

    if (tabs[activeTab] && !isObject) {
      localStorage.removeItem("selectedSection");
      localStorage.setItem("selectedComponent", tabs[activeTab].component);
    }
  }, [activeTab, tabs]);

  /* Unsaved Handler */
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingActionType, setPendingActionType] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const mainCompRef = useRef();
  const detailsPlRef = useRef();
  const faAdditionRef = useRef();
  const faRegisterRef = useRef();
  const getCurrentRef = (sectionKey) => {
    switch (sectionKey) {
      case "detailPL":
        return detailsPlRef;
      case "mainComputationData":
        return mainCompRef;
      case "FAAdditions":
        return faAdditionRef;
      case "FARegister":
        return faRegisterRef;
      default:
        return null;
    }
  };
  const checkUnsavedChanges = (sectionKey, action, label = null) => {
    const currentRef = getCurrentRef(sectionKey);

    if (currentRef && currentRef !== undefined && currentRef !== null) {
      if (
        !currentRef.current?.isDirty?.() &&
        !currentRef.current?.shouldConfirmExit?.()
      ) {
        action();
        return;
      }
      setPendingAction(() => action);
      setPendingActionType(label);

      if (currentRef.current?.isDirty?.()) {
        setSaveDialogOpen(true);
      } else if (currentRef.current?.shouldConfirmExit?.()) {
        setRemainDialogOpen(true);
      }
    } else {
      action();
    }
  };

  const handleSaveAndRemain = async () => {
    const { sectionKey } = getCurrentTab();
    const currentRef = getCurrentRef(sectionKey);
    if (!currentRef?.current?.getUnsavedData) {
      handleError("Missing save handler. Please try again.");
      setPendingAction(null);
      return;
    }

    const data = currentRef.current.getUnsavedData();
    if (!data || Object.keys(data).length === 0) {
      handleError(
        "The unsaved data is missing or incomplete. Please try again."
      );
      setPendingAction(null);
      return;
    }

    try {
      await handleSave(sectionKey, [states, data], authContext, {
        onSuccess: handleSuccess,
        onError: handleError,
      });

      currentRef.current.clearDirtyFlag();
      setSaveDialogOpen(false);
    } catch (error) {
      handleError(error);
    }
  };
  const handleSaveAndProceed = async () => {
    const { sectionKey } = getCurrentTab();
    const currentRef = getCurrentRef(sectionKey);
    if (!currentRef?.current?.getUnsavedData) {
      handleError("Missing save handler. Please try again.");
      setPendingAction(null);
      return;
    }

    const data = currentRef.current.getUnsavedData();
    if (!data || Object.keys(data).length === 0) {
      handleError(
        "The unsaved data is missing or incomplete. Please try again."
      );
      setPendingAction(null);
      return;
    }

    try {
      await handleSave(sectionKey, [states, data], authContext, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
      currentRef.current.clearDirtyFlag();
      setSaveDialogOpen(false);
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleCancelProceed = () => {
    setSaveDialogOpen(false);
    if (pendingAction) {
      setPendingAction(null);
    }
  };
  const handleConfirmLeave = async () => {
    try {
      setSaveDialogOpen(false);
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const [remainDialogOpen, setRemainDialogOpen] = useState(false);
  const handleStayOnCurrentPage = () => {
    setRemainDialogOpen(false); // Close the dialog
    if (pendingAction) {
      setPendingAction(null); // Clear pending action
    }
  };
  const handleNavigateToNewPage = () => {
    setRemainDialogOpen(false); // Close the dialog
    if (pendingAction) {
      pendingAction(); // Execute the stored action
      setPendingAction(null); // Clear pending action
    }
  };
  /* Unsaved Handler */

  /* Navigation Handler */
  const handleDrawerNavigation = async (
    item,
    toggleScheduleMenu,
    toggleCaScheduleMenu
  ) => {
    const currentTab = getCurrentTab();
    if (!["P&L Schedules", "CA Schedules"].includes(item.label))
      checkUnsavedChanges(
        currentTab.sectionKey,
        () => {
          const existingTabIndex = tabs.findIndex(
            (tab) =>
              tab.label === item.label && tab.sectionKey === item.sectionKey
          );

          if (existingTabIndex !== -1) {
            setActiveTab(existingTabIndex);
          } else {
            const newTab = {
              label: item.label,
              component: item.component,
              sectionKey: item.sectionKey,
            };

            setTabs((prevTabs) => {
              const updatedTabs = [...prevTabs, newTab];
              setActiveTab(updatedTabs.length - 1);
              return updatedTabs;
            });
          }
        },
        item.label
      );
    else {
      if (item.label === "P&L Schedules") {
        toggleScheduleMenu();
      } else if (item.label === "CA Schedules") {
        toggleCaScheduleMenu();
      }
    }
  };

  const handleTabChange = (_, newValue) => {
    const currentTab = getCurrentTab();
    const nextTab = tabs[newValue];
    checkUnsavedChanges(
      currentTab.sectionKey,
      async () => {
        setActiveTab(newValue);
      },
      nextTab?.label
    );
  };

  const handleCloseTab = (index) => {
    setTabs((prevTabs) => {
      const updatedTabs = [...prevTabs];
      updatedTabs.splice(index, 1);

      setActiveTab((prevActive) => {
        if (updatedTabs.length === 0) return 0;
        if (prevActive === index) {
          return index === 0 ? 0 : index - 1;
        } else if (prevActive > index) {
          return prevActive - 1;
        }
        return prevActive;
      });

      return updatedTabs;
    });
  };
  /* Navigation Handler */

  /* Schedules */
  const [originalSchedules, setOriginalSchedules] = useState([]);
  const [originalCASchedules, setOriginalCASchedules] = useState([]);

  const [fromScheduleData, setFromScheduleData] = useState(null);
  const [targetScheduleNo, setTargetScheduleNo] = useState(0);

  const [fromCAScheduleData, setFromCAScheduleData] = useState(null);
  const [targetCAScheduleNo, setTargetCAScheduleNo] = useState("");

  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderCADialogOpen, setReorderCADialogOpen] = useState(false);

  //Create Schedule Dialogs
  const [schCreateDialogOpen, setSchCreateDialogOpen] = useState(false);
  const [CACreationdialogOpen, setCACreationDialogOpen] = useState(false);
  const [isScheduleCreationLoading, setIsScheduleCreationLoading] =
    useState(false);

  const [plFormData, setPlFormData] = useState({
    scheduleType: "",
    scheduleTitle: "",
  });
  const handleOpenSchCreateDialog = () => {
    setSchCreateDialogOpen(true);
    setPlFormData({ scheduleType: "", scheduleTitle: "" });
  };
  const handleFormChange = (name, value) => {
    setPlFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [CAFormData, setCAFormData] = useState({
    scheduleTypes: [],
  });
  const handleOpenCaCreateDialog = () => {
    setCACreationDialogOpen(true);
    setCAFormData({ scheduleTypes: [] });
  };
  const handleCAFormChange = (name, value) => {
    setCAFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreatePlSchedule = async () => {
    if (!plFormData.scheduleType || !plFormData.scheduleTitle.trim()) {
      handleError("Please select a schedule type and enter a title.");
      return;
    }

    setIsScheduleCreationLoading(true);
    try {
      const response = await createPLSchedule({
        tc_id: company.tc_id,
        scheduleType: plFormData.scheduleType,
        scheduleTitle: plFormData.scheduleTitle,
      });

      if (response.status === "success") {
        const fetchedSchedules = await getSchedules(company.tc_id);
        setSchedules(fetchedSchedules);

        const createdScheduleId = response.data?.sch_id;
        return (
          fetchedSchedules.find(
            (schedule) =>
              String(schedule.sch_id) === String(createdScheduleId)
          ) || {
            sch_id: createdScheduleId,
            scheduleNo: response.data?.schedule_no,
            scheduleTitle:
              response.data?.schedule_title || plFormData.scheduleTitle,
            scheduleType:
              response.data?.schedule_type ||
              plFormData.scheduleType.replace(/\s+/g, ""),
          }
        );
      } else {
        handleError("Failed to create schedule. Please try again.");
      }
    } catch (error) {
      if (error.response?.status === 409)
        handleError("The chosen P&L schedule has already been created.");
      else handleError(error);
    } finally {
      setIsScheduleCreationLoading(false);
    }

    return null;
  };

  const handleCreateCASchedule = async () => {
    if (!CAFormData.scheduleTypes || CAFormData.scheduleTypes.length <= 0) {
      handleError("Please select atleast one schedule type to create.");
      return;
    }

    setIsScheduleCreationLoading(true);
    try {
      const scheduleParams = {
        tc_id: company.tc_id,
      };

      // Add scheduleType if it matches special types
      scheduleParams.checklist = CAFormData.scheduleTypes;

      const updatedCASchedules = await createCASchedule(scheduleParams);

      if (updatedCASchedules) {
        handleSuccess("CA Schedule created successfully");
        // Refresh the CA schedules list
        const fetchedCASchedules = await getCAScheduleList(company.tc_id);
        setCASchedules(fetchedCASchedules);
      }

      setCACreationDialogOpen(false);
    } catch (error) {
      if (error.response?.status === 409)
        handleError("The chosen CA schedule has already been created.");
      else handleError(error);
    } finally {
      setIsScheduleCreationLoading(false);
    }
  };

  const reorderSchedule = (data) => {
    setFromScheduleData(data);
    setReorderDialogOpen(true);
  };

  const reorderCASchedule = (data) => {
    setFromCAScheduleData(data);
    setReorderCADialogOpen(true);
  };

  const moveSchedule = (type, dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) return;

    if (type === "P&L") {
      if (!originalSchedules.length) {
        setOriginalSchedules([...schedules]);
      }

      const updated = [...schedules];
      const [dragged] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, dragged);
      setTargetScheduleNo(schedules[hoverIndex].scheduleNo);
      setSchedules(updated);
    } else if (type === "CA") {
      if (!originalCASchedules.length) {
        setOriginalCASchedules([...CASchedules]);
      }

      const updated = [...CASchedules];
      const [dragged] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, dragged);
      setTargetCAScheduleNo(CASchedules[hoverIndex].scheduleNo);
      setCASchedules(updated);
    }
  };

  const handleCancelReorder = async (type) => {
    if (type === "P&L") {
      setReorderDialogOpen(false);
      setIsFetchingSchedules(true);

      if (originalSchedules.length) {
        setSchedules([...originalSchedules]);
        setOriginalSchedules([]);
      }

      setFromScheduleData(null);
      setTargetScheduleNo(0);
      setIsFetchingSchedules(false);
    } else if (type === "CA") {
      setReorderCADialogOpen(false);
      setIsFetchingCASchedules(true);

      if (originalCASchedules.length) {
        setCASchedules([...originalCASchedules]);
        setOriginalCASchedules([]);
      }

      setFromCAScheduleData(null);
      setTargetCAScheduleNo("");
      setIsFetchingCASchedules(false);
    }
  };

  const handleConfirmReorder = async () => {
    if (fromScheduleData && targetScheduleNo > 0) {
      try {
        setIsReorderingSchedules(true);
        setReorderDialogOpen(false);
        const response = await reorderScheduleNo({
          tc_id: company.tc_id,
          sch_id: fromScheduleData.id,
          targetIndex: targetScheduleNo,
        });

        if (response) {
          handleSuccess("Schedule order updated successfully");
        }
        setFromScheduleData(null);
        setTargetScheduleNo(0);
        setOriginalSchedules([]);
      } catch (error) {
        handleError(error);
      } finally {
        refreshSchedules("PL", company.tc_id);
        setIsReorderingSchedules(false);
      }
    }
  };

  const handleConfirmReorderCA = async () => {
    if (fromCAScheduleData && targetCAScheduleNo !== "") {
      try {
        setIsReorderingSchedules(true);
        setReorderCADialogOpen(false);
        const response = await reorderCAScheduleNo({
          tc_id: company.tc_id,
          sch_id: fromCAScheduleData.id,
          targetIndex: targetCAScheduleNo,
        });

        if (response) {
          handleSuccess("Schedules reordered successfully");
        }
        setFromCAScheduleData(null);
        setTargetCAScheduleNo("");
        setOriginalCASchedules([]);
      } catch (error) {
        handleError(error);
      } finally {
        refreshSchedules("CA", company.tc_id);
        setIsReorderingSchedules(false);
      }
    }
  };

  const handleOpenSchedule = async (type, schedule, scheduleType) => {
    const currentTab = getCurrentTab();
    checkUnsavedChanges(
      currentTab.sectionKey,
      async () => {
        const newTab = {
          label: `${schedule.scheduleNo} - ${schedule.scheduleTitle}`,
          key: `${schedule.sch_id}`,
          sectionKey: scheduleType,
          id: schedule.sch_id,
          component: {
            schedule,
            type,
            scheduleType:
              type === "CA" ? scheduleType : scheduleType.replace(/\s+/g, ""),
          },
        };

        setTabs((prevTabs) => {
          const existingTabIndex = prevTabs.findIndex(
            (tab) => tab.key === `${schedule.sch_id}`
          );

          if (existingTabIndex !== -1) {
            setActiveTab(existingTabIndex);
            return prevTabs;
          } else {
            const updated = [...prevTabs, newTab];
            setActiveTab(updated.length - 1); // safe because updated is the new array
            return updated;
          }
        });
        // Update localStorage
        localStorage.setItem(
          "selectedSection",
          type === "CA" ? "CA Schedule" : "Schedule"
        );
        localStorage.setItem("selectedComponent", JSON.stringify(schedule));
        localStorage.setItem("scheduleType", scheduleType);
      },
      scheduleType
    );
  };

  const openScheduleWithDelay = (schedule, scheduleType, delay = true) => {
    const typeLabel = scheduleType === "FAAdditions" ? "CA" : "P&L";

    const runner = () => handleOpenSchedule(typeLabel, schedule, scheduleType);

    if (delay) {
      setTimeout(runner, 0);
    } else {
      runner();
    }
  };
  /* Schedules */

  /* Render Component */
  const renderComponent = (componentName) => {
    // Check if component should be rendered based on company type
    if (!shouldRenderComponent(company, componentName)) {
      const componentLabel =
        typeof componentName === "object"
          ? componentName.type === "CA"
            ? "CA Schedule"
            : "Schedule"
          : componentName;
      return (
        <AccessDenied
          title='Component Not Available'
          message={`This ${componentLabel} is not available for ${
            company.companyType === "investment" ? "investment" : "this"
          } company type.`}
          showBackButton={false}
        />
      );
    }

    if (componentName !== null && typeof componentName === "object") {
      const currentRef = getCurrentRef(componentName.scheduleType);
      const SelectedComponent =
        componentName.type === "CA"
          ? CAScheduleComponents[componentName.scheduleType]
          : scheduleComponents[componentName.scheduleType];

      const props = {
        company,
        scheduleId: componentName.schedule.sch_id,
        scheduleType: componentName.scheduleType,
        onOpenSchedule: (schedule, scheduleType, key) =>
          handleOpenSchedule("P&L", schedule, scheduleType, key),
        onOpenCASchedule: (schedule, scheduleType, key) =>
          handleOpenSchedule("CA", schedule, scheduleType, key),
        openScheduleWithDelay,
        handleCloseTab,
        uen: company.uen,
        detailsId: company.tc_id,
        companyName: company.name,
        yearOfAssessment: company.yearOfAssessment,
        financialYearStart: company.financialYearStart,
        financialYearEnd: company.financialYearEnd,
        currency: company.currency,
        currencyRate: company.currencyRate,
      };

      if (currentRef && currentRef !== null) {
        props.ref = currentRef;
      }

      return <SelectedComponent {...props} />;
    }

    // Render predefined components based on string names
    switch (componentName) {
      case "Content Page":
        return (
          <ContentPage
            states={states}
            company={company}
            yearOfAssessment={company.yearOfAssessment}
            checkAccess={checkAccess}
            plSchedules={schedules}
            caSchedules={CASchedules}
            isFetching={isFetchingSchedules}
            handleError={handleError}
            handleSuccess={handleSuccess}
            reorderSchedule={reorderSchedule}
            moveSchedule={(dragIndex, hoverIndex) => {
              moveSchedule("P&L", dragIndex, hoverIndex);
            }}
            getPlScheduleDoc={getPlScheduleDoc}
            getCaScheduleDoc={getCaScheduleDoc}
            fetchStatementDocs={getStatementDoc}
            fetchCASummaryPdfDetails={fetchCASummaryPdfDetails}
            onAddSchedule={handleOpenSchCreateDialog}
            onAddCaSchedule={handleOpenCaCreateDialog}
            onOpenSchedule={(schedule, scheduleType) =>
              handleOpenSchedule("P&L", schedule, scheduleType)
            }
            onOpenCaSchedule={(schedule, scheduleType) =>
              handleOpenSchedule("CA", schedule, scheduleType)
            }
            reorderCaSchedule={reorderCASchedule}
            moveCaSchedule={(dragIndex, hoverIndex) => {
              moveSchedule("CA", dragIndex, hoverIndex);
            }}
          />
        );
      case "Main Computation":
        return <MainTaxComputation company={company} ref={mainCompRef} />;
      case "Detailed P&L":
        return (
          <DetailedPL
            company={company}
            ref={detailsPlRef}
            openScheduleWithDelay={openScheduleWithDelay}
          />
        );
      default:
        return <WorkInProgress />;
    }
  };
  /* Render Component */

  return (
    <DndProvider backend={HTML5Backend}>
      {loading || accessLoading ? (
        <Loading />
      ) : (
        <Box
          display='flex'
          flexDirection={"row"}
          height='100vh'
          sx={{
            overflow: "hidden", // Prevent main container from scrolling
          }}
        >
          <NavigationPanel
            company={company}
            open={drawerOpen}
            tabs={tabs}
            onNavClick={handleDrawerNavigation}
            onToggleDrawer={toggleDrawer}
            schedules={schedules}
            CASchedules={CASchedules}
            isFetchingSchedules={isFetchingSchedules}
            isFetchingCASchedules={isFetchingCASchedules}
            schMenuActions={{
              onCreatePlSchedule: handleOpenSchCreateDialog,
              onCreateCaSchedule: handleOpenCaCreateDialog,
              onOpenSchedule: handleOpenSchedule,
              reorderSchedule: reorderSchedule,
              reorderCASchedule: reorderCASchedule,
              moveSchedule: moveSchedule,
            }}
            onOpenDisclosureLibrary={() => setDisclosureLibraryOpen(true)}
          />

          <ScheduleTaggingProvider
            company={company}
            onSchedulesChanged={refreshSchedules}
          >
            <ConsolePanel
              theme={theme}
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              onCloseTab={handleCloseTab}
              renderComponent={renderComponent}
            />
          </ScheduleTaggingProvider>

          <AutoSaveConfirmationDialog
            open={saveDialogOpen}
            label={pendingActionType || null}
            onCancel={handleCancelProceed}
            onSave={handleSaveAndRemain}
            onLeave={handleConfirmLeave}
            onConfirm={handleSaveAndProceed}
            section={getCurrentTab().label}
          />

          <NavigationConfirmationDialog
            open={remainDialogOpen}
            currentPage={getCurrentTab().label}
            destination={pendingActionType}
            onCancel={handleStayOnCurrentPage}
            onStay={handleStayOnCurrentPage}
            onNavigate={handleNavigateToNewPage}
            title='Navigation Confirmation'
            description={`You are currently on ${getCurrentTab().label}.`}
          />

          <ScheduleCreationDialog
            company={company}
            open={schCreateDialogOpen}
            onClose={() => setSchCreateDialogOpen(false)}
            onSubmit={handleCreatePlSchedule}
            title='Add New P&L Schedule'
            isLoading={isScheduleCreationLoading}
            formData={plFormData}
            onFormChange={handleFormChange}
            onOpenSchedule={(schedule) => {
              setSchCreateDialogOpen(false);
              handleOpenSchedule("P&L", schedule, schedule.scheduleType);
            }}
          />

          <CAScheduleCreationDialog
            open={CACreationdialogOpen}
            onClose={() => setCACreationDialogOpen(false)}
            onSubmit={handleCreateCASchedule}
            title='Add New CA Schedule'
            schedules={CASchedules}
            caSettings={caSettings}
            isLoading={isScheduleCreationLoading}
            formData={CAFormData}
            onFormChange={handleCAFormChange}
          />

          <CustomDialog
            open={
              !!(fromScheduleData && targetScheduleNo > 0 && reorderDialogOpen)
            }
            type='warning'
            onClose={() => handleCancelReorder("P&L")}
            onConfirm={handleConfirmReorder}
            title='Confirm Schedule Reordering'
            description={`You're about to move "${fromScheduleData?.scheduleTitle}" from Sch No. ${fromScheduleData?.scheduleNo} to Sch No. ${targetScheduleNo}. Do you want to proceed?`}
          />

          <CustomDialog
            open={
              !!(
                fromCAScheduleData &&
                targetCAScheduleNo !== "" &&
                reorderCADialogOpen
              )
            }
            type='warning'
            onClose={() => handleCancelReorder("CA")}
            onConfirm={handleConfirmReorderCA}
            title='Confirm CA Schedule Reordering'
            description={`You're about to move "${fromCAScheduleData?.scheduleTitle}" from Sch No. ${fromCAScheduleData?.scheduleNo} to Sch No. ${targetCAScheduleNo}. Do you want to proceed?`}
          />

          <PageLoadingOverlay
            open={isReorderingSchedules}
            message='Updating schedule numbers...'
            subMessage='Please wait while we reorder your schedules'
          />

          <GlobalDisclosureLibrary
            company={company}
            open={disclosureLibraryOpen}
            onOpenChange={setDisclosureLibraryOpen}
          />
        </Box>
      )}
    </DndProvider>
  );
};

export default TaxComputationConsole;
